// =============================================================================
// Action registry.
//
// An Action is a scoped operation a user can invoke. Actions are registered
// here once and consumed by any UI that cares — the AI on Demand workspace,
// mode-specific panels (WellFinder, etc.), context menus on AOIs.
//
// Adding a new action: append an entry to ACTIONS and implement its run +
// preview functions in a sibling file.
//
// Shape:
//   {
//     id:          stable slug
//     kind:        'report' | 'model_run' | 'export' | 'query'
//     name:        display name
//     description: one-sentence description
//     icon:        lucide-react component
//     preview:     { layers, getCount, countLabel }  (optional)
//     run({ scope, fetchWithAuth }): async; returns a result object or throws
//   }
// =============================================================================

import { FileText, Radar, Crosshair } from 'lucide-react';

import {
  runWellfinderReport,
  getWellfinderReportScopeCount,
  listWellfinderCounties,
  COUNTY_FILTERS,
} from './wellfinderReport.js';

import {
  runPadDetection,
  getPadDetectionScopeCount,
} from './padDetection.js';

import {
  runWellheadDetection,
  getWellheadDetectionScopeCount
} from './wellheadDetection.js';

export const ACTIONS = [
  {
    id: 'wellfinder_report',
    kind: 'report',
    name: 'WellFinder Report',
    description: 'Generate the WellFinder deliverable for a saved AOI — a Word report plus a companion geodatabase, in one archive. Detection tiers, registry attribution, and QC review state.',
    icon: FileText,
    tone: 'primary',
    // This report is scoped to a SAVED AOI and cannot run on the viewport.
    // Its figures — area, bounding box, county, and the detection filter itself
    // — are all derived from one stored geometry, so the scope it states and
    // the scope it counted are the same object. A viewport has no stored
    // geometry to derive them from, and it changes with window size and zoom,
    // so the same request would not reproduce. ActionPreview reads this flag
    // and blocks the run with an explanation; nothing else in the panel does.
    requiresAoi: true,
    requiresAoiReason: 'This report needs a saved AOI or a county. Pick an AOI under Scope above, or switch to County — a viewport changes with window size and zoom, so the report could not be reproduced.',
    // OR ONE COUNTY (2026-10-30). ActionPreview offers a County picker for an
    // action that declares this; a picked county satisfies requiresAoi. The
    // forced options are shown locked and sent as such.
    countyScope: {
      listCounties: listWellfinderCounties,
      forcedOptions: COUNTY_FILTERS,
      forcedNote: 'A county report always leaves out non-producing RRC matches. The count above is the report\'s.',
    },
    preview: {
      layers: ['wells'],
      getCount: getWellfinderReportScopeCount,
      countLabel: (n) => (n === 1 ? 'AI-detected well' : 'AI-detected wells'),
    },
    // On/off switches ActionPreview renders and passes to run() as `options`.
    // Keys are the bundle route's `filters` keys, validated strictly there.
    options: [
      {
        key: 'rrc_producing_match',
        label: 'Leave out non-producing RRC matches',
        // The preview count comes from a different route and is NOT filtered;
        // said here so the number above the switch is not read as the report's.
        description: 'Leave out detections matched to an RRC well the registry does not report as producing. Detections with no RRC match are kept. The count above includes all detections.',
        default: false,
      },
    ],
    run: runWellfinderReport,
  },
  {
    id: 'pad_detection',
    kind: 'model_run',
    name: 'Pad Detection',
    description: 'Run the pad detection model on the imagery source you select within your scope to detect pads. Results render on the map when complete.',
    icon: Radar,
    tone: 'primary',
    needsImagerySelection: true,   // renders the entitled-imagery pulldown; run sends dataset_id
    preview: {
      layers: [],
      getCount: getPadDetectionScopeCount,
      countLabel: (n) => (n === 1 ? 'imagery tile' : 'imagery tiles'),
    },
    run: runPadDetection,
  },
  {
    id: 'wellhead_detection',
    kind: 'model_run',
    name: 'Wellhead Detection',
    description: 'Run the wellhead detection model on the imagery source you select within your scope to detect individual wellheads. Results render on the map as points when complete.',
    icon: Crosshair,
    tone: 'primary',
    displayColor: '#fbbf24',  // amber
    needsImagerySelection: true,   // renders the entitled-imagery pulldown; run sends dataset_id
    preview: {
      layers: [],
      getCount: getWellheadDetectionScopeCount,
      countLabel: (n) => (n === 1 ? 'imagery tile' : 'imagery tiles'),
    },
    run: runWellheadDetection,
  },
];

export function getActionById(id) {
  return ACTIONS.find((a) => a.id === id) ?? null;
}

export function getActionsByKind(kind) {
  return ACTIONS.filter((a) => a.kind === kind);
}